import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Markdown, ArrowLink } from "components";
import { useAnchor } from "hooks";
import { Features23Content } from "types/graphql/generated";
import { ImagesContainer } from "./components";

interface Props {
  content: Features23Content;
}

const Features23: VFC<Props> = withErrorBoundary(
  ({ content: { title, description, list, features23Label, features23Link, ...imagesContainerContent } }) => {
    useAnchor("features23");

    return (
      <StyledFeatures23 className="features23 Content">
        <div className="Features23__mainContent">
          <h2 className="Features23__title Font_48_54_600 lg:Font_40_48_600 sm:Font_32_40_600">{title}</h2>
          <Markdown className="Features23__description Color_secondary Font_24_32_450 sm:Font_18_26_450">
            {description}
          </Markdown>
          <div className="Features23__features">
            <div className="Features23__featuresContainer">
              {list.map(({ number, description, id }) => (
                <div key={id} className="Features23__feature">
                  <p className="Features23__stepNumber Font_18_26_700">{number}</p>
                  <Markdown className="Features23__stepDescription Font_24_32_450 sm:Font_18_26_450">
                    {description}
                  </Markdown>
                </div>
              ))}
              <ArrowLink href={features23Link} className="Features23__link Font_18_26_600">
                {features23Label}
              </ArrowLink>
            </div>
            <ImagesContainer content={imagesContainerContent} className="Features23__imagesContainerClone" />
          </div>
        </div>
        <ImagesContainer content={imagesContainerContent} className="Features23__imagesContainer" />
      </StyledFeatures23>
    );
  },
  {}
);

const StyledFeatures23 = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: -64px;
  padding-top: 64px;

  .Features23__imagesContainer {
    display: flex;
    margin-left: 160px;
  }

  .Features23__imagesContainerClone {
    display: none;
  }

  .Features23__mainContent {
    display: flex;
    flex-direction: column;
    max-width: 664px;
  }

  .Features23__description {
    margin-top: 16px;
  }

  .Features23__features {
    max-width: 600px;
    margin-top: 64px;
  }

  .Features23__feature {
    display: flex;
  }

  .Features23__feature:not(:first-child) {
    margin-top: 40px;
  }

  .Features23__stepNumber {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    border: 1px solid #282828;
  }

  .Features23__stepDescription {
    margin-left: 24px;
  }

  .Features23__link {
    margin-top: 40px;

    svg {
      margin-top: 0.08em;
      width: 14px;
      height: 14px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.xl}px`}) {
    .Features23__mainContent {
      max-width: none;
    }

    .Features23__features {
      display: flex;
      max-width: none;
    }

    .Features23__imagesContainerClone {
      display: flex;
      margin-left: 76px;
    }

    .Features23__imagesContainer {
      display: none;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .Features23__imagesContainerClone {
      margin-left: 16px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .Features23__features {
      margin-top: 48px;
    }

    .Features23__imagesContainerClone {
      display: none;
    }
  }
`;

gql`
  fragment Features23Content on ComponentFeaturesFeatures23 {
    title
    description
    features23Label
    features23Link
    list {
      id
      number
      description
    }
    ...ImagesContainerContent
  }
`;

export { Features23 };
export type { Props as Features23Props };
